/**
 *  ===============================================================================================
 *     ____ _____ _   _          _______     __                 _____           _     _    _
 *    |  _ \_   _| \ | |   /\   |  __ \ \   / /                / ____|         | |   | |  | |
 *    | |_) || | |  \| |  /  \  | |__) \ \_/ /__  _ __   ___  | |  __ _ __ ___ | |__ | |__| |
 *    |  _ < | | | . ` | / /\ \ |  _  / \   / _ \| '_ \ / _ \ | | |_ | '_ ` _ \| '_ \|  __  |
 *    | |_) || |_| |\  |/ ____ \| | \ \  | | (_) | | | |  __/ | |__| | | | | | | |_) | |  | |
 *    |____/_____|_| \_/_/    \_\_|  \_\ |_|\___/|_| |_|\___|  \_____|_| |_| |_|_.__/|_|  |_|
 *
 *  ===============================================================================================
 * @author BINARY one GmbH
 * @license Unlicensed
 */
import '/src/scss/index.scss';
import { BASEURL, SITEURL } from '@libTs/cms.globals';

import { createApp, defineAsyncComponent, h } from 'vue';

// Eager components — above the fold, used on most pages, or lightweight.
import Hamburger from './components/Hamburger.vue';
import QuickSearch from './components/QuickSearch.vue';
import TextBox from './components/TextBox.vue';
import Register from './components/Register.vue';
import ReadTime from './components/ReadTime.vue';
import TeamFilter from './components/TeamFilter.vue';
import TeamCard from './components/TeamCard.vue';
import Ankerlinks from './components/Ankerlinks.vue';
import SpeedDial from './components/SpeedDial.vue';
import Slider from '@libVue/components/Slider.vue';
import Feature from './components/Feature.vue';

// Async components — pull heavy vendor deps (naive-ui, vue3-google-map) or are
// rarely used. Split into their own chunks so most pages never download them.
// errorComponent covers a failed chunk load (e.g. blocked/offline network) so
// the reserved layout space doesn't stay blank whitespace with a silent
// console error.
const chunkLoadError = (message: string) => ({
  render: () =>
    h(
      'div',
      { class: 'flex items-center justify-center p-6 text-center' },
      message,
    ),
});

const Search = defineAsyncComponent({
  loader: () => import('./components/Search.vue'),
  errorComponent: chunkLoadError('Suche ist aktuell nicht verfügbar.'),
});
const ExchangeRates = defineAsyncComponent({
  loader: () => import('./components/ExchangeRates.vue'),
  errorComponent: chunkLoadError('Wechselkurse sind aktuell nicht verfügbar.'),
});
const GoogleMap = defineAsyncComponent({
  loader: () => import('./components/GoogleMap.vue'),
  errorComponent: chunkLoadError('Karte ist aktuell nicht verfügbar.'),
});

import { initExternalLinks } from './utils/externalLinks';
import { initImageFallback } from './utils/imageFallback';
import { initScrollDepth } from './utils/scrollDepth';
import { initConsent } from './utils/consent';

// Directive imports

// Service imports

// -----------------------------------------------------------
// DEBUG output
const ISDEBUG = import.meta.env.DEV;

initConsent();
initExternalLinks();
initImageFallback();
initScrollDepth();

if (ISDEBUG) {
  console.group('Environment loaded');
  console.log(ISDEBUG);
  console.log(
    ' -----------------------------------\n',
    ` BASEURL:\t\t${BASEURL}\n`,
    ` SITEURL:\t\t${SITEURL}\n`,
    '-----------------------------------',
  );
  console.groupEnd();
}

const app = createApp({
  setup() {},
  data() {
    return {};
  },
  provide() {
    return {};
  },
  components: {
    Hamburger,
    QuickSearch,
    TextBox,
    Register,
    ReadTime,
    Slider,
    TeamFilter,
    TeamCard,
    Ankerlinks,
    SpeedDial,
    Feature,
    Search,
    ExchangeRates,
    GoogleMap,
  },
  directives: {},
});

app.mount('#app');

// Reveal the live header/speed-dial and retire the server-rendered static
// placeholders now that the components own the DOM (see critical.twig).
document.documentElement.classList.add('app-ready');

// Defer GSAP-powered scroll/scroll-trigger animations out of the critical path.
// Each module self-initialises on DOMContentLoaded/load, so importing them after
// `load` keeps gsap in idle-time chunks without breaking initialisation.
window.addEventListener('load', async () => {
  await Promise.all([
    import('./components/TimelineScroll'),
    import('./components/Accordion'),
    import('./components/CounterAnimation'),
    import('./components/WaveAnimation'),
  ]);

  // One coordinated refresh after every module has registered its triggers,
  // replacing the per-module setTimeout refreshes.
  const { ScrollTrigger } = await import('gsap/ScrollTrigger');
  ScrollTrigger.refresh();
});
